/**
 * @file   src\Pages\AddBuyUps.tsx
 * @brief  AddBuyUps page.
 * @date   JAN, 2026
 * @author ZCO Engineer
 * @copyright (c) 2026, ZCO
 */

import {
  useState,
  useRef,
  useEffect,
  Container,
  Button,
  Breadcrumb,
  Row,
  Col,
  useOutletContext,
  useNavigate,
  useParams,
  React,
  useLocation,
} from "../utils/thirdpartyComponents";
import "../styles/subscriptions.scss";
import CWInput from "../components/CWInput";
import CWInputGroup from "../components/CWInputGroup";
import { pageURLs } from "../utils/pageUrls";
import * as Yup from "yup";
import { MessageToaster } from "../utils/toastUtil";
import {
  AMOUNT_MAX_LENGTH,
  WASH_NAME_MAX_LENGTH,
} from "../utils/constants";
import { AppContextType } from "../interfaces/generalInterface";
import CWMultiSelect from "../components/CWMultiSelect";
import axios from "../config/axiosConfig";
import { CompanyApi, LocationApi, SubscriptionApi } from "../utils/apiUrls";
import { LocationItem, Option } from "../interfaces/subscriptionInterface";
import GenerateLink from "../components/GenerateLink";
import CarWashStatus from "../components/CarWashStatus";
import SmallLoader from "../components/SmallLoader";
import { userType } from "../utils/enums";
import { addBuyUpsSchema } from "../validations/addBuyUpsSchema";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { Essentials, Paragraph, Bold, Italic, Heading } from "ckeditor5";
import ClassicEditor from "../utils/classicEditor";

interface BuyUpsDataItem {
  location: Option[];
  washname: string;
  buyupDetails: string;
  amount: string;
}

const AddBuyUps = (props: any) => {
  const carwash_owner =
    props.userType === userType["CARWASH-OWNER"] ? true : false;
  const [locations, setLocations] = useState<Option[]>([]);
  const [companyDetails, setCompanyDetails] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<Option[]>([]);
  const formRef = useRef<HTMLFormElement>(null);
  const buyupDetailsEditorRef = useRef<any>(null);
  const { updateData } = useOutletContext<AppContextType>();
  const navigate = useNavigate();
  const param = useParams();
  const location = useLocation();
  const currentBayType = location?.state?.bayType ?? 0;
  const edit = props.edit === true;
  const carWashId = carwash_owner
    ? null
    : parseInt((param as any).carWashId ?? param.id ?? "0");
  const buyupId = edit
    ? carwash_owner
      ? parseInt(param.id ?? "0")
      : parseInt((param as any).buyupId ?? param.id ?? "0")
    : 0;
  const toaster = new MessageToaster();

  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const [formData, setFormData] = useState<BuyUpsDataItem>({
    location: [],
    washname: "",
    buyupDetails: "",
    amount: "",
  });

  const [errors, setErrors] = useState({
    location: "",
    washname: "",
    buyupDetails: "",
    amount: "",
  });

  useEffect(() => {
    getCompany();
    getCompanyAddress();
  }, []);

  useEffect(() => {
    handleUpdate();
  }, [company]);

  useEffect(() => {
    const editor = buyupDetailsEditorRef.current;
    if (!editor) {
      return;
    }

    const currentData = editor.getData();
    if (currentData !== (formData.buyupDetails || "")) {
      editor.setData(formData.buyupDetails || "");
    }
  }, [formData.buyupDetails]);

  const handleUpdate = () => {
    updateData({
      heading: carwash_owner
        ? edit
          ? "Edit Up Sell"
          : "Add Up Sell"
        : company.companyName,
      url: carwash_owner
        ? `/${pageURLs.manage_subscriptions}?type=buyups&bayType=${currentBayType}`
        : `/superadmin/${pageURLs.manage_subscriptions}/${carWashId}?type=buyups&bayType=${currentBayType}`,
      showBackButton: true,
      showMainMenu: true,
      activeLink: carwash_owner ? "manage-subscriptions" : "manage-car-wash",
    });
  };

  const getCompany = async () => {
    try {
      const param1 = `${carWashId}`;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS)
        : await axios.get(CompanyApi.GET_COMPANY + param1);

      await getCompanyLocations(data.data.carWashGUID);
      setCompanyDetails(data.data);
    } catch (error: any) {
      console.error("Error fetching company information:", error.message);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const getCompanyAddress = async () => {
    try {
      const id = carWashId ?? 0;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id);
      if (data?.error === 0) {
        setCompany(data.data);
      }
      setLoading(false);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const getCompanyLocations = async (carwashGuid: string) => {
    try {
      const { data } = carwash_owner
        ? await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS)
        : await axios.get(LocationApi.GET_COMPANY_LOCATIONS + carwashGuid);
      if (data.error === 0) {
        const location_list: LocationItem[] = data?.data;

        const transformedArray: Option[] = location_list.map((item) => ({
          label: item.locationName,
          value: item.locationId,
        }));

        setLocations(transformedArray);
      } else {
        toaster.toastError(data.message);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const handleMultiSelectChange = (selectedOption: Option[], name: string) => {
    setSelectedLocation(selectedOption);

    if (Object.keys(errors).includes(name)) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }

    setFormData({
      ...formData,
      [name]: selectedOption,
    });
  };

  useEffect(() => {
    if (!edit || !locations.length || !buyupId) {
      return;
    }
    const loadBuyupDetails = async () => {
      try {
        setLoading(true);
        const detailsUrl = carwash_owner
          ? SubscriptionApi.CARWASH_OWNER_GET_BUY_UP_DETAILS
          : SubscriptionApi.GET_BUY_UP_DETAILS;
        const { data } = await axios.get(`${detailsUrl}${buyupId}`);
        const responseData = data?.data;
        const detail = Array.isArray(responseData) ? responseData[0] : responseData;
        if (data?.error === 0 && detail) {
          const locationIdSet = new Set(
            String(detail?.LocationIDs ?? "")
              .split(",")
              .map((id: string) => Number(id.trim()))
              .filter((id: number) => Number.isFinite(id))
          );
          const selected = locations.filter((loc) =>
            locationIdSet.has(Number(loc.value))
          );
          setSelectedLocation(selected);
          setFormData({
            location: selected,
            washname: detail.BuyupName || "",
            buyupDetails: detail.BuyupDetails || "",
            amount: String(detail.BuyUpPrice ?? ""),
          });
        } else {
          toaster.toastError(data?.message || "Failed to load Up Sell details.");
        }
      } catch (error: any) {
        toaster.toastError(
          error?.response?.data?.message || error?.message || "Failed to load."
        );
      } finally {
        setLoading(false);
      }
    };
    loadBuyupDetails();
  }, [edit, carwash_owner, locations, buyupId]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;

    if (Object.keys(errors).includes(name)) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }

    if (name === "amount") {
      const regex = /^\d{0,8}(\.\d{0,2})?$/;
      if (regex.test(value)) {
        setFormData({
          ...formData,
          [name]: value,
        });
      }
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await addBuyUpsSchema.validate(formData, { abortEarly: false });

      const locationIdArray = formData.location.map((loc) => loc.value);
      const payload = carwash_owner
        ? {
            ...(edit ? { buyupId } : {}),
            locationIds: locationIdArray,
            buyupName: formData.washname,
            buyupDetails: formData.buyupDetails,
            price: parseFloat(formData.amount),
          }
        : {
            ...(edit ? { buyupId } : {}),
            carWashId,
            locationIds: locationIdArray,
            buyupName: formData.washname,
            buyupDetails: formData.buyupDetails,
            price: parseFloat(formData.amount),
          };

      setLoading(true);
      const { data } = carwash_owner
        ? edit
          ? await axios.put(
              SubscriptionApi.CARWASH_OWNER_UPDATE_BUY_UPS,
              payload
            )
          : await axios.post(
              SubscriptionApi.CARWASH_OWNER_ADD_BUY_UPS,
              payload
            )
        : edit
          ? await axios.put(SubscriptionApi.UPDATE_BUY_UPS, payload)
          : await axios.post(SubscriptionApi.ADD_BUY_UPS, payload);
      setLoading(false);

      if (data?.error === 0) {
        setErrors({
          location: "",
          washname: "",
          buyupDetails: "",
          amount: "",
        });
        const redirectUrl = carwash_owner
          ? `/${pageURLs.manage_subscriptions}?type=buyups&bayType=${currentBayType}`
          : `/superadmin/${pageURLs.manage_subscriptions}/${carWashId}?type=buyups&bayType=${currentBayType}`;

        const nextBuyupId = edit ? buyupId : data?.data?.result ?? null;
        toaster.toastSuccess(
          data?.message 
        );
        if (!nextBuyupId) {
          navigate(redirectUrl);
          return;
        }
        if (!carwash_owner) {
          navigate(
            `/superadmin/${pageURLs.add_buyup_relaynumber}/${carWashId}/${nextBuyupId}?bayType=${currentBayType}`,
            { state: { type: "buyups" } }
          );
        } else {
          navigate(
            `/${pageURLs.add_buyup_relaynumber}/${nextBuyupId}?bayType=${currentBayType}`,
            {
              state: { type: "buyups" },
            }
          );
        }
      } else {    
        toaster.toastError(data?.message);
      }
    } catch (error) {
      const newErrors: any = {};
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach((err) => {
          newErrors[err.path as keyof typeof formData] = err.message;
        });
        setErrors(newErrors);
      }

      if (formRef.current) {
        const firstInvalidField = Object.keys(newErrors)[0];
        const invalidElement =
          formRef.current.querySelector<HTMLInputElement>(
            `[name="${firstInvalidField}"]`
          );
        if (invalidElement) {
          invalidElement.focus();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const itemClick = async (url: string) => {
    navigate(url);
  };

  return (
    <div className="manage-carwash-container manage-subscription-container">
      <Container>
        <Breadcrumb>
          {carwash_owner ? null : (
            <>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(`/superadmin/${pageURLs.manage_carwash}`);
                }}
              >
                Manage Car Washes
              </Breadcrumb.Item>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(`/superadmin/${pageURLs.carwash_locations}/${carWashId}`);
                }}
              >
                {company.companyName}
              </Breadcrumb.Item>
            </>
          )}

          <Breadcrumb.Item
            onClick={() => {
              const url = carwash_owner
                ? `/${pageURLs.manage_subscriptions}?type=buyups&bayType=${currentBayType}`
                : `/superadmin/${pageURLs.manage_subscriptions}/${param.id}?type=buyups&bayType=${currentBayType}`;
              itemClick(url);
            }}
          >
            Manage Up Sell
          </Breadcrumb.Item>

          <Breadcrumb.Item active>{edit ? "Edit Up Sell" : "Add Up Sell"}</Breadcrumb.Item>
        </Breadcrumb>

        {carwash_owner ? null : (
          <div className="manage-activity-wrap d-flex justify-content-between align-items-center">
            {companyDetails && (
              <GenerateLink id={param.id} companyDetails={companyDetails} carwash_owner={carwash_owner} />
            )}
            <div className="activity-status d-flex">
              <CarWashStatus
                carWashId={carWashId}
                status={companyDetails?.IsCompanyActive == 1 ? true : false}
                companyName={company.companyName}
              />
            </div>
          </div>
        )}

        <div className="cw-form-outer-wrap">
          <form onSubmit={(e) => handleSubmit(e)} ref={formRef}>
            <div className="cw-form-container">
              <div className="form-inner mx-auto">
                <div>
                  <CWMultiSelect
                    id="location"
                    name="location"
                    options={locations}
                    label="Select Location"
                    dropdownGap={0}
                    dropdownPosition="auto"
                    isMandatory
                    placeholder="Select Location"
                    errorMessage={errors.location}
                    onChange={(selectedOptions) =>
                      handleMultiSelectChange(selectedOptions, "location")
                    }
                    hasSelectAll={true}
                    value={selectedLocation}
                    labelAllSelected="All Locations Selected."
                  />
                </div>
                <Row>
                  <Col md={6}>
                    <CWInput
                      id="washname"
                      name="washname"
                      type="text"
                      label="Up Sell Name"
                      maxLength={WASH_NAME_MAX_LENGTH}
                      isMandatory
                      value={formData.washname}
                      errorMessage={errors.washname}
                      onChange={handleInputChange}
                    />
                  </Col>
                  <Col md={6}>
                    <div className="form-group cw-input">
                      <div className="cust-label-group">
                        <label htmlFor="label">
                          Up Sell details
                          <span className="mandatory">*</span>
                        </label>
                        <CKEditor
                          editor={ClassicEditor as any}
                          onReady={(editor) => {
                            buyupDetailsEditorRef.current = editor;
                            editor.setData(formData.buyupDetails || "");
                          }}
                          config={{
                            licenseKey: "GPL",
                            plugins: [Essentials, Paragraph, Bold, Italic, Heading],
                            toolbar: [
                              "heading",
                              "|",
                              "bold",
                              "italic",
                              "underline",
                              "strikethrough",
                              "|",
                              "link",
                              "bulletedList",
                              "numberedList",
                              "blockQuote",
                              "|",
                              "undo",
                              "redo",
                              "|",
                              "alignment",
                              "fontSize",
                              "fontColor",
                              "fontBackgroundColor",
                              "highlight",
                              "codeBlock",
                              "insertTable",
                              "imageUpload",
                              "mediaEmbed",
                            ],
                            heading: {
                              options: [
                                {
                                  model: "paragraph",
                                  title: "Paragraph",
                                  class: "ck-heading_paragraph",
                                },
                                {
                                  model: "heading1",
                                  view: "h1",
                                  title: "Heading 1",
                                  class: "ck-heading_heading1",
                                },
                                {
                                  model: "heading2",
                                  view: "h2",
                                  title: "Heading 2",
                                  class: "ck-heading_heading2",
                                },
                                {
                                  model: "heading3",
                                  view: "h3",
                                  title: "Heading 3",
                                  class: "ck-heading_heading3",
                                },
                                {
                                  model: "heading4",
                                  view: "h4",
                                  title: "Heading 4",
                                  class: "ck-heading_heading4",
                                },
                              ],
                            },
                            image: {
                              toolbar: [
                                "imageTextAlternative",
                                "toggleImageCaption",
                                "imageStyle:full",
                                "imageStyle:side",
                              ],
                            },
                            table: {
                              contentToolbar: [
                                "tableColumn",
                                "tableRow",
                                "mergeTableCells",
                              ],
                            },
                          }}
                          onChange={(event, editor) => {
                            const data = editor.getData();
                            setFormData((prev) => ({
                              ...prev,
                              buyupDetails: data,
                            }));
                            setErrors({
                              ...errors,
                              ["buyupDetails"]: "",
                            });
                          }}
                        />
                        <span className="error-msg">{errors.buyupDetails}</span>
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <CWInputGroup
                      id="amount"
                      name="amount"
                      type="number"
                      step="0.01"
                      label="Amount"
                      labelPrefix="$"
                      isMandatory
                      maxLength={AMOUNT_MAX_LENGTH}
                      value={formData.amount}
                      errorMessage={errors.amount}
                      onChange={handleInputChange}
                    />
                  </Col>
                </Row>
              </div>
            </div>
            <div className="btn-sec d-flex justify-content-end">
              <Button
                title="Cancel"
                variant="primary"
                className="secondary-btn"
                onClick={() => {
                  const url = carwash_owner
                    ? `/${pageURLs.manage_subscriptions}?type=buyups&bayType=${currentBayType}`
                    : `/superadmin/${pageURLs.manage_subscriptions}/${carWashId}?type=buyups&bayType=${currentBayType}`;
                  navigate(url);
                }}
              >
                Cancel
              </Button>
              <Button
                title="Next"
                variant="primary"
                className="primary-btn"
                type="submit"
              >
                Next
              </Button>
            </div>
          </form>
        </div>
      </Container>
      {loading && <SmallLoader />}
    </div>
  );
};

export default AddBuyUps;

